/**
 * SOLID Review
 * S — Strong. One job: a 4-digit code, one digit per box, and its submission. Which server call it is, the check and signing in are
 *     the `verifyCode` use case's (strict-ports FE3); this holds the digits, the busy flag and the message.
 * O — Acceptable. A 6-digit code would mean changing `DIGITS` here and the Domain's length.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. A digit typed, a key pressed, submit, back — focus is the view's, asked for by box.
 * D — Strong. Two use cases, the client view and the request contexts.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import type { ClientRequests } from '../client/ClientRequests';
import { messageFor } from '../client/failureMessage';
import type { IPortClientStateView } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

/** Both sign-up and sign-in originate from the unified email screen. */
const BACK_TO_EMAIL: TClientUseCaseInput<'navigate'> = { screen: 'LOGIN' };
/** The Domain's refusal of a code that is not four digits: the digits stay for the player to fix. */
const INVALID_INPUT = 'INVALID_INPUT';
const DIGITS: readonly string[] = Object.freeze(['', '', '', '']);

/** What the code is for — the screen's one prop. */
export type TVerifyPurpose = 'VERIFY_EMAIL' | 'LOGIN';

/** Moves the focus to a box, by its index — the view's (its inputs are its own). */
export type TFocusBox = (index: number) => void;

interface IStateVerifyCodeOwn {
  readonly digits: readonly string[];
  readonly isLoading: boolean;
  readonly error: string | null;
}

/** The screen as its view renders it: the digits, the busy flag, the message, and the address the code went to. */
export interface IStateVerifyCode extends IStateVerifyCodeOwn {
  readonly pendingEmail: string | null;
}

const EMPTY: IStateVerifyCodeOwn = { digits: DIGITS, isLoading: false, error: null };
const BUSY: Partial<IStateVerifyCodeOwn> = { error: null, isLoading: true };
const IDLE: Partial<IStateVerifyCodeOwn> = { isLoading: false };
const CLEARED: Partial<IStateVerifyCodeOwn> = { digits: DIGITS };

/**
 * THE CODE'S DIGITS (frontend-framework-oo P3, 2026-10-29, §28.295) — `components/auth/VerifyCodeScreen.tsx`'s state and handlers: a
 * digit typed moves on to the next box, Backspace in an empty box moves back, a refused code is cleared (unless it was only
 * malformed) and the first box asked for again — the view moves the focus, at the moments the screen did.
 */
export class VerifyCodeViewModel implements IServiceViewModel<IStateVerifyCode> {
  private readonly state = new ObservableState<IStateVerifyCodeOwn>(EMPTY);
  private shown: { readonly own: IStateVerifyCodeOwn; readonly pendingEmail: string | null; readonly screen: IStateVerifyCode } | null = null;

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'verifyCode' | 'navigate'>,
    private readonly view: IPortClientStateView,
    private readonly requests: ClientRequests,
  ) {}

  readonly changeDigit = (index: number, typed: string, focus: TFocusBox): void => {
    const single = typed.replace(/\D/g, '').slice(-1);
    const digits = [...this.state.get().digits];
    digits[index] = single;
    const changed: Partial<IStateVerifyCodeOwn> = { digits };
    this.state.update(changed);
    if (single && index < DIGITS.length - 1) focus(index + 1);
  };

  readonly keyDown = (index: number, key: string, focus: TFocusBox): void => {
    if (key === 'Backspace' && !this.state.get().digits[index] && index > 0) focus(index - 1);
  };

  readonly submit = async (purpose: TVerifyPurpose, focus: TFocusBox): Promise<void> => {
    if (!this.view.getSnapshot().session.pendingEmail) return;
    this.state.update(BUSY);
    try {
      const command: TClientUseCaseInput<'verifyCode'> = { purpose, code: this.state.get().digits.join('') };
      const verified = await this.useCases.verifyCode(this.requests.context(), command);
      if (!verified.success) {
        const refused: Partial<IStateVerifyCodeOwn> = { error: messageFor(verified.error, 'Invalid code. Please try again.') };
        this.state.update(refused);
        if (verified.error.code !== INVALID_INPUT) {
          this.state.update(CLEARED);
          focus(0);
        }
      }
    } finally {
      this.state.update(IDLE);
    }
  };

  readonly back = (): void => {
    void this.useCases.navigate(this.requests.context(), BACK_TO_EMAIL);
  };

  getSnapshot(): IStateVerifyCode {
    const own = this.state.get();
    const pendingEmail = this.view.getSnapshot().session.pendingEmail;
    if (this.shown !== null && this.shown.own === own && this.shown.pendingEmail === pendingEmail) return this.shown.screen;
    const screen: IStateVerifyCode = { ...own, pendingEmail };
    this.shown = { own, pendingEmail, screen };
    return screen;
  }

  subscribe(listener: () => void): () => void {
    const leaveOwn = this.state.subscribe(listener);
    const leaveView = this.view.subscribe(listener);
    return () => {
      leaveOwn();
      leaveView();
    };
  }

  attach(): void {}

  detach(): void {}
}
